import {
	Fragment, useMemo, useState
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import {
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from '@mui/material';
import {
	ChevronDown, ChevronRight, Download, ExternalLink, Filter, List, ListTree, Package,
	RefreshCcw
} from 'lucide-react';
import BackdropLoader from '../../../Components/Loaders/BackdropLoader';
import { urls } from '../../../Constants';
import { downloadCSV, filtersInfoChip } from '@/Helpers';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import GlobalSearch from '@/Components/globalSearch/search';
import drawerActions from '@/Actions/drawer.actions';
import { Button } from '@/Components/ui/button';
import kitIssuanceServices from '../../../Services/kit-issuance.services';

const DATE_FORMAT = 'YYYY-MM-DD';

// one tab per order jsonData.typ the API returns
const KIT_TABS = [
	{
		value: 'ingKit', label: 'Ingredient Issuance',
	},
	{
		value: 'transferIntermediate', label: 'SFG Issuance',
	},
];

// the listing as kits (a row per kit) or item-wise (a row per required item, broken down by the
// kits it is needed in). In the URL as `kitView`, next to `kitTab`.
const VIEWS = [
	{
		value: 'kits', label: 'Kit-wise', icon: Package,
	},
	{
		value: 'items', label: 'Item-wise', icon: ListTree,
	},
];

// the view toggle stands out next to the status filter: primary outline, filled when picked
const VIEW_TOGGLE_SX = {
	'& .MuiToggleButton-root': {
		textTransform: 'none',
		fontWeight: 600,
		fontSize: '0.875rem',
		px: 2,
		color: 'primary.main',
		borderColor: 'primary.main',
	},
	'& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
		bgcolor: 'primary.main',
		color: 'common.white',
	},
};

// the same filter sidebar as FMS Orders List: kitchen is mandatory, then a scheduled date
// range or production plans (picking plans greys the dates out and they are not sent)
const FILTERS_INFO = {
	header: {
		store: false,
		date: false,
		statusAndTypes: false,
		process: false,
		product: false,
	},
	store: {
		text: 'Kitchen *',
		show: true,
		mandatory: true,
		noAllOption: true,
	},
	dateRangeWithType: {
		text: 'Scheduled Date',
		show: true,
	},
	productionPlan: {
		text: 'Production Plans',
		show: true,
	},
};

const TIME_TYPES = [
	{
		label: 'Scheduled At', value: 'scheduledAt',
	},
];

// the name an item is grouped, sorted and searched by
const itemName = (item) => {
	return String(item?.name ?? '').trim() || '-';
};

// search bar field picker (same GlobalSearch as FMS Orders List), filtering the loaded kits.
// Item Name keeps the kits needing a matching item; item-wise it also shows only matching items.
const ITEM_NAME_FIELD = 'itemName';

// the first option is GlobalSearch's default field
const SEARCH_OPTIONS = [
	{
		label: 'Kit Name', value: 'kitName', placeholder: 'Search by Kit Name',
	},
	{
		label: 'Order ID', value: 'orderId', placeholder: 'Search by Order ID',
	},
	{
		label: 'Item Name', value: ITEM_NAME_FIELD, placeholder: 'Search by Required Item Name',
	},
	{
		label: 'Plan', value: 'productionPlanName', placeholder: 'Search by Production Plan',
	},
	{
		label: 'Process', value: 'process', placeholder: 'Search by Issuing Process / Issued To',
	},
];

const searchFields = (kit, field) => {
	if (field === 'process') return [kit?.fromProcessName, kit?.toProcessName];
	if (field === ITEM_NAME_FIELD) return (kit?.items || []).map(itemName);
	return [kit?.[field]];
};

const STATUSES = [
	{
		value: 'ALL', label: 'All Statuses',
	},
	{
		value: 'PENDING', label: 'Pending',
	},
	{
		value: 'COMPLETED', label: 'Completed',
	},
];

const collator = new Intl.Collator(undefined, {
	numeric: true,
	sensitivity: 'base',
});

const formatDateTime = (val, format = 'DD-MMM-YYYY hh:mm a') => {
	if (!val) return '-';
	return moment(val, 'YYYY-MM-DD HH:mm:ss').format(format);
};

const roundQty = (qty) => {
	return Math.round(Number(qty) * 1000) / 1000;
};

const formatQty = (qty, unit) => {
	if (qty === null || qty === undefined) return '-';
	const rounded = roundQty(qty);
	return unit ? `${rounded} ${unit}` : `${rounded}`;
};

// isIssued comes from the API: consumed with a measured qty above 0. Same rule here when a backend
// that predates the flag does not send it, so such rows are not all shown as not issued.
const isItemIssued = (item) => {
	return item?.isIssued ?? (!!item?.isConsumed && Number(item?.measuredQty) > 0);
};

const itemStatus = (item) => {
	if (item?.issuedInOtherKit) return 'Issued in other kit';
	if (item?.skipMeasurement) return 'Skipped';
	if (isItemIssued(item)) return 'Issued';
	if (item?.isConsumed) return 'Not issued (0 qty)';
	return 'Pending';
};

const itemStatusColor = (item) => {
	if (item?.skipMeasurement || item?.issuedInOtherKit) return 'default';
	if (isItemIssued(item)) return 'success';
	return item?.isConsumed ? 'error' : 'warning';
};

// distinct non-empty values, alphabetical: the section chips
const uniqueSorted = (values) => {
	return [...new Set(values.filter(Boolean))].sort(collator.compare);
};

// qty totals per unit, so an item planned in two units reads "1200 gram + 3 kg", not a wrong sum
const sumByUnit = (lines, field) => {
	const totals = new Map();
	lines.forEach(({ item }) => {
		const raw = item?.[field];
		const val = Number(raw);
		if (raw === null || raw === undefined || Number.isNaN(val)) return;
		const unit = item?.unit || '';
		totals.set(unit, (totals.get(unit) || 0) + val);
	});
	return [...totals.entries()].map(([unit, qty]) => {
		return {
			unit,
			qty,
		};
	});
};

const formatTotals = (totals) => {
	if (totals.length === 0) return '-';
	return totals.map(({ unit, qty }) => { return formatQty(qty, unit); }).join(' + ');
};

// within an item: by kit name, then scheduled time, then order
const byKit = (a, b) => {
	return collator.compare(a.kit?.kitName ?? '', b.kit?.kitName ?? '') ||
		String(a.kit?.scheduledAt ?? '').localeCompare(String(b.kit?.scheduledAt ?? '')) ||
		(Number(a.kit?.orderId) || 0) - (Number(b.kit?.orderId) || 0);
};

// one group per required item (A-Z), each listing every kit line it is needed in.
// An issued-in-other-kit line is issued (and measured) through another kit, so it is listed but
// left out of the totals and the issued count, which would otherwise count that qty a second time.
// Skipped lines count toward the required total (the item is still needed) but not the issued
// count, the same rows the kit Completion % leaves out.
// nameFilter: lower-case text the item name must contain ('' = every item)
const buildItemGroups = (kits, nameFilter = '') => {
	const groups = new Map();
	kits.forEach((kit) => {
		(kit?.items || []).forEach((item) => {
			const name = itemName(item);
			if (nameFilter && !name.toLowerCase().includes(nameFilter)) return;
			if (!groups.has(name)) groups.set(name, []);
			groups.get(name).push({
				kit,
				item,
			});
		});
	});
	return [...groups.entries()]
		.sort(([a], [b]) => { return collator.compare(a, b); })
		.map(([name, lines]) => {
			lines.sort(byKit);
			const counted = lines.filter(({ item }) => { return !item?.issuedInOtherKit; });
			const toIssue = counted.filter(({ item }) => { return !item?.skipMeasurement; });
			return {
				name,
				lines,
				kitsCount: new Set(lines.map(({ kit }) => { return kit?.id; })).size,
				required: sumByUnit(counted, 'qty'),
				measured: sumByUnit(counted, 'measuredQty'),
				issuedCount: toIssue.filter(({ item }) => { return isItemIssued(item); }).length,
				toIssueCount: toIssue.length,
			};
		});
};

// ---- CSV export of whichever view is showing, after every filter ----

const toCsvCell = (value) => {
	const str = value === null || value === undefined ? '' : String(value);
	return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toCsv = (header, rows) => {
	return [header, ...rows].map((row) => { return row.map(toCsvCell).join(','); }).join('\n');
};

// numbers stay numbers in Excel; the unit goes in its own column
const csvQty = (qty) => {
	return qty === null || qty === undefined || qty === '' ? '' : roundQty(qty);
};

const KIT_EXPORT_HEADER = [
	'Order ID', 'Kit Name', 'Issued To', 'Issuing Process', 'Plan', 'Plan ID', 'Scheduled At',
	'Completed', 'Completion %', 'Items Issued', 'Items Total',
];

const kitExportRows = (kits) => {
	return kits.map((kit) => {
		return [
			kit.orderId, kit.kitName, kit.fromProcessName, kit.toProcessName, kit.productionPlanName,
			kit.productionPlanId, kit.scheduledAt, kit.isCompleted ? 'Yes' : 'No', kit.completionPercentage,
			kit.itemsIssued, kit.itemsTotal,
		];
	});
};

const ITEM_EXPORT_HEADER = [
	'Required Item', 'Used In (Kit)', 'Issued To', 'Issuing Process', 'Order ID', 'Plan', 'Plan ID',
	'Scheduled At', 'Required Qty', 'Measured Qty', 'Unit', 'Status', 'Issued At',
];

const itemExportRows = (groups) => {
	return groups.flatMap((group) => {
		return group.lines.map(({ kit, item }) => {
			return [
				group.name, kit.kitName, kit.fromProcessName, kit.toProcessName, kit.orderId,
				kit.productionPlanName, kit.productionPlanId, kit.scheduledAt, csvQty(item.qty),
				csvQty(item.measuredQty), item.unit, itemStatus(item), item.consumedAt,
			];
		});
	});
};

// a kit's items - the Kit Items dialog and the hover card on the Completion column
const KitItemsTable = ({ items }) => {
	return (
		<Table size='small'>
			<TableHead>
				<TableRow>
					<TableCell>Item</TableCell>
					<TableCell align='right'>Required Qty</TableCell>
					<TableCell align='right'>Measured Qty</TableCell>
					<TableCell>Status</TableCell>
					<TableCell>Issued At</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{(items || []).length === 0 && (
					<TableRow>
						<TableCell colSpan={5} align='center'>
							No items in this kit.
						</TableCell>
					</TableRow>
				)}
				{(items || []).map((item) => {
					return (
						<TableRow key={item.id}>
							<TableCell>{item.name || '-'}</TableCell>
							<TableCell align='right'>{formatQty(item.qty, item.unit)}</TableCell>
							<TableCell align='right'>{formatQty(item.measuredQty, item.unit)}</TableCell>
							<TableCell>
								<Chip
									size='small'
									color={itemStatusColor(item)}
									label={itemStatus(item)}
								/>
							</TableCell>
							<TableCell>{formatDateTime(item.consumedAt)}</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
};

// MUI's dark tooltip styled as a white card wide enough for the item table
const ITEMS_TOOLTIP_PROPS = {
	tooltip: {
		sx: {
			bgcolor: 'common.white',
			color: 'text.primary',
			maxWidth: 'none',
			boxShadow: 4,
			border: '1px solid #e5e7eb',
			p: 1,
		},
	},
	arrow: {
		sx: {
			color: 'common.white',
		},
	},
};

// sections (the process a kit is issued to) as multi-select chips: click to pick / unpick, none
// picked = all. They narrow the already loaded listing, apart from the search bar and the filter
// drawer. A picked section the loaded kits no longer have stays shown, so it can be unpicked.
const SectionChips = ({ options, counts, value, onChange }) => {
	const picked = new Set(value);
	const shown = uniqueSorted([...options, ...value]);
	if (shown.length === 0) return null;
	const toggle = (section) => {
		onChange(picked.has(section)
			? value.filter((val) => { return val !== section; })
			: [...value, section]);
	};
	return (
		<div className='flex flex-wrap items-center gap-2'>
			<span className='text-sm font-medium text-gray-600'>Sections:</span>
			<Chip
				size='small'
				color='primary'
				label='All'
				variant={value.length === 0 ? 'filled' : 'outlined'}
				onClick={() => { return onChange([]); }}
			/>
			{shown.map((section) => {
				const isPicked = picked.has(section);
				return (
					<Chip
						key={section}
						size='small'
						color='primary'
						label={`${section} (${counts[section] || 0})`}
						variant={isPicked ? 'filled' : 'outlined'}
						onClick={() => { return toggle(section); }}
						onDelete={isPicked ? () => { return toggle(section); } : undefined}
					/>
				);
			})}
		</div>
	);
};

const ITEM_TABLE_COLUMNS = 9;

// item-wise view: a header row per required item with its totals, then the kits it is needed in
const ItemWiseTable = ({ groups, expanded, onToggle, emptyMessage }) => {
	return (
		<TableContainer component={Paper}>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell>Required Item</TableCell>
						<TableCell>Used In (Kit)</TableCell>
						<TableCell>Issued To</TableCell>
						<TableCell>Order ID</TableCell>
						<TableCell>Plan</TableCell>
						<TableCell>Scheduled Date</TableCell>
						<TableCell align='right'>Required Qty</TableCell>
						<TableCell align='right'>Measured Qty</TableCell>
						<TableCell>Status</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{groups.length === 0 && (
						<TableRow>
							<TableCell colSpan={ITEM_TABLE_COLUMNS} align='center'>
								{emptyMessage}
							</TableCell>
						</TableRow>
					)}
					{groups.map((group) => {
						const isOpen = !!expanded[group.name];
						return (
							<Fragment key={group.name}>
								<TableRow
									hover
									onClick={() => { return onToggle(group.name); }}
									sx={{
										bgcolor: 'grey.100',
										cursor: 'pointer',
									}}
								>
									<TableCell colSpan={6}>
										<div className='flex items-center gap-2'>
											{isOpen ? <ChevronDown className='size-4' /> : <ChevronRight className='size-4' />}
											<span className='font-semibold'>{group.name}</span>
											<span className='text-xs text-gray-500'>
												{`in ${group.kitsCount} ${group.kitsCount === 1 ? 'kit' : 'kits'}`}
											</span>
										</div>
									</TableCell>
									<TableCell align='right'>
										<span className='font-semibold'>{formatTotals(group.required)}</span>
									</TableCell>
									<TableCell align='right'>
										<span className='font-semibold'>{formatTotals(group.measured)}</span>
									</TableCell>
									<TableCell>
										<span className='text-xs text-gray-600'>
											{`${group.issuedCount}/${group.toIssueCount} issued`}
										</span>
									</TableCell>
								</TableRow>
								{isOpen && group.lines.map(({ kit, item }) => {
									return (
										<TableRow key={`${kit.id}-${item.id}`}>
											<TableCell />
											<TableCell>{kit.kitName || '-'}</TableCell>
											<TableCell>{kit.fromProcessName || '-'}</TableCell>
											<TableCell>{kit.orderId}</TableCell>
											<TableCell>
												{kit.productionPlanName || '-'}
												<div className='text-xs text-gray-500'>
													{`#${kit.productionPlanId}`}
												</div>
											</TableCell>
											<TableCell>{formatDateTime(kit.scheduledAt)}</TableCell>
											<TableCell align='right'>{formatQty(item.qty, item.unit)}</TableCell>
											<TableCell align='right'>{formatQty(item.measuredQty, item.unit)}</TableCell>
											<TableCell>
												<Chip
													size='small'
													color={itemStatusColor(item)}
													label={itemStatus(item)}
												/>
											</TableCell>
										</TableRow>
									);
								})}
							</Fragment>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

const KitIssuance = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { pathname, search } = useLocation();
	const query = useMemo(() => {
		return new URLSearchParams(search);
	}, [search]);

	// not `tab` - ImagesFinder keeps its own filter state in the URL and owns that key
	const activeTab = KIT_TABS.find((tab) => { return tab.value === query.get('kitTab'); }) ?? KIT_TABS[0];
	const activeView = VIEWS.find((view) => { return view.value === query.get('kitView'); }) ?? VIEWS[0];
	const isItemView = activeView.value === 'items';

	const [kits, setKits] = useState([]);
	const [filterValue, setFilterValue] = useState({});
	const [isLoading, setLoading] = useState(false);
	// GlobalSearch's selection: { value: field, searchValue: text }
	const [searchBy, setSearchBy] = useState({});
	const [status, setStatus] = useState('ALL');
	const [itemsKit, setItemsKit] = useState(null);
	// the section chips over the loaded listing: the process a kit is issued to (fromProcessName)
	const [sections, setSections] = useState([]);
	// item-wise view: item names opened to show their kit lines; items start collapsed (totals only)
	const [expandedItems, setExpandedItems] = useState({});

	const openFilters = () => {
		dispatch(drawerActions.toggleFiltersDrawer(true));
	};

	// ImagesFinder's onSubmit; it also fires once on load (from the URL, or with defaults)
	const fetchKits = async (filters) => {
		setFilterValue(filters);
		const storeId = filters?.store?.id;
		if (!storeId) {
			// first visit: nothing picked yet, the drawer asks for the kitchen
			setKits([]);
			openFilters();
			return;
		}
		const planIds = filters?.productionPlan?.id ?? [];
		try {
			setLoading(true);
			const res = await kitIssuanceServices.list({
				data: planIds.length > 0 ? {
					storeId,
					productionPlanIds: planIds,
				} : {
					storeId,
					dateFrom: moment(filters?.dateRange?.from).format(DATE_FORMAT),
					dateTo: moment(filters?.dateRange?.to).format(DATE_FORMAT),
				},
			});
			setKits(res?.statusCode === 200 ? (res?.data || []) : []);
		} catch (err) {
			// handleResponse already raised the error notification
			// eslint-disable-next-line no-console
			console.error(err);
			setKits([]);
		} finally {
			setLoading(false);
		}
	};

	const setUrlParam = (key, value) => {
		const params = new URLSearchParams(search);
		params.set(key, value);
		history.replace({
			pathname,
			search: params.toString(),
		});
	};

	const handleTabChange = (_e, value) => {
		setUrlParam('kitTab', value);
		// sections differ between the ingredient and SFG kits
		setSections([]);
		setExpandedItems({});
	};

	const handleViewChange = (_e, value) => {
		// ToggleButtonGroup sends null when the selected button is clicked again
		if (value) setUrlParam('kitView', value);
	};

	// kits per tab, before search / status, for the tab labels
	const tabCounts = useMemo(() => {
		return kits.reduce((acc, kit) => {
			acc[kit?.kitType] = (acc[kit?.kitType] || 0) + 1;
			return acc;
		}, {});
	}, [kits]);

	// the section chips offer what the loaded kits of this tab have, with their kit counts, before
	// search / status like the tab labels
	const tabKits = useMemo(() => {
		return kits.filter((kit) => { return kit?.kitType === activeTab.value; });
	}, [kits, activeTab]);

	const sectionCounts = useMemo(() => {
		return tabKits.reduce((acc, kit) => {
			if (kit?.fromProcessName) acc[kit.fromProcessName] = (acc[kit.fromProcessName] || 0) + 1;
			return acc;
		}, {});
	}, [tabKits]);

	const sectionOptions = useMemo(() => {
		return uniqueSorted(Object.keys(sectionCounts));
	}, [sectionCounts]);

	const searchText = (searchBy?.searchValue ?? '').trim().toLowerCase();
	const searchField = searchBy?.value ?? SEARCH_OPTIONS[0].value;

	const filteredKits = useMemo(() => {
		const sectionSet = new Set(sections);
		return tabKits.filter((kit) => {
			if (status === 'COMPLETED' && !kit?.isCompleted) return false;
			if (status === 'PENDING' && kit?.isCompleted) return false;
			if (sectionSet.size > 0 && !sectionSet.has(kit?.fromProcessName)) return false;
			if (!searchText) return true;
			return searchFields(kit, searchField).some((val) => {
				return String(val ?? '').toLowerCase().includes(searchText);
			});
		});
	}, [tabKits, searchText, searchField, status, sections]);

	const completedCount = useMemo(() => {
		return filteredKits.filter((kit) => {
			return kit?.isCompleted;
		}).length;
	}, [filteredKits]);

	// an Item Name search also drops the non-matching items of the matching kits
	const itemNameFilter = searchField === ITEM_NAME_FIELD ? searchText : '';

	const itemGroups = useMemo(() => {
		return isItemView ? buildItemGroups(filteredKits, itemNameFilter) : [];
	}, [isItemView, filteredKits, itemNameFilter]);

	const allExpanded = itemGroups.length > 0 &&
		itemGroups.every((group) => { return expandedItems[group.name]; });

	const toggleItem = (name) => {
		setExpandedItems((prev) => {
			return {
				...prev,
				[name]: !prev[name],
			};
		});
	};

	const toggleAllItems = () => {
		if (allExpanded) {
			setExpandedItems({});
			return;
		}
		setExpandedItems(Object.fromEntries(itemGroups.map((group) => {
			return [group.name, true];
		})));
	};

	const exportCount = isItemView ? itemGroups.length : filteredKits.length;

	// what was fetched (plans, or the date range) and the tab / view, e.g.
	// Kit_Issuance_Ingredient_Issuance_Item_Wise_2026-10-12_to_2026-10-13.csv
	const exportFileName = () => {
		const planIds = filterValue?.productionPlan?.id ?? [];
		const scope = planIds.length > 0
			? `Plans_${planIds.join('-')}`
			: `${moment(filterValue?.dateRange?.from).format(DATE_FORMAT)}_to_${moment(filterValue?.dateRange?.to).format(DATE_FORMAT)}`;
		const tabPart = activeTab.label.replace(/\s+/g, '_');
		return `Kit_Issuance_${tabPart}_${isItemView ? 'Item_Wise' : 'Kit_Wise'}_${scope}.csv`;
	};

	const handleExport = () => {
		const csv = isItemView
			? toCsv(ITEM_EXPORT_HEADER, itemExportRows(itemGroups))
			: toCsv(KIT_EXPORT_HEADER, kitExportRows(filteredKits));
		downloadCSV(csv, exportFileName());
	};

	// same page the Cooking History "View" button opens, in a new tab
	const handleView = (kit) => {
		const path = `${urls.EXTENSION}${urls.PAST_QC}/${kit?.productId ?? kit?.recipeId}?id=${kit?.recipeId}&type=CMS`;
		window.open(path, '_blank', 'noopener');
	};

	const emptyMessage = () => {
		if (isLoading) return 'Loading...';
		if (!filterValue?.store?.id) {
			return 'Select a kitchen and a date range or production plan from Filters.';
		}
		if (isItemView) {
			return `No ${activeTab.label.toLowerCase()} items for the selected filters.`;
		}
		return `No ${activeTab.label.toLowerCase()} kits for the selected filters.`;
	};

	return (
		<div className='p-4'>
			<BackdropLoader isLoading={isLoading} showProgress />
			{/* header + search row laid out like FMS Orders List: search fills the row; refresh, export
			and the filter drawer button sit at the right end */}
			<div className='flex flex-col gap-2 justify-between w-full p-2'>
				<div className='flex justify-between items-center gap-2'>
					<span className='font-bold text-[16px]'>
						Kit Issuance
					</span>
				</div>
				<div className='flex gap-3 w-full justify-start items-center py-1'>
					<div className='flex-1 min-w-0'>
						<GlobalSearch
							dropDownOptions={SEARCH_OPTIONS}
							setSelectedValue={setSearchBy}
						/>
					</div>
					<Tooltip title='Refresh Kits'>
						<RefreshCcw
							className={`size-5 ${filterValue?.store?.id ? 'cursor-pointer' : 'opacity-40'}`}
							onClick={() => {
								if (filterValue?.store?.id) fetchKits(filterValue);
							}}
						/>
					</Tooltip>
					<Tooltip title={`Download the ${activeView.label.toLowerCase()} list as shown, as CSV`}>
						<span>
							<Button
								variant='outline'
								onClick={handleExport}
								disabled={exportCount === 0}
								className='flex justify-center gap-2 px-4 py-2'
							>
								<span className='text-nowrap'>Export</span>
								<Download className='size-4' />
							</Button>
						</span>
					</Tooltip>
					<Button
						onClick={openFilters}
						className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700'
					>
						<span className='text-nowrap'>Filters</span>
						<Filter className='size-4' />
					</Button>
				</div>
			</div>
			{filtersInfoChip(FILTERS_INFO, filterValue)}
			<ImagesFinder
				filtersInfo={FILTERS_INFO}
				onSubmit={fetchKits}
				timeTypeArray={TIME_TYPES}
				exportInfo={{
					hideExportAccordian: true,
				}}
			/>

			<div className='flex flex-wrap justify-between items-center gap-3 mb-2 border-b border-gray-200'>
				<Tabs
					value={activeTab.value}
					onChange={handleTabChange}
				>
					{KIT_TABS.map((tab) => {
						return (
							<Tab
								key={tab.value}
								value={tab.value}
								label={`${tab.label} (${tabCounts[tab.value] || 0})`}
							/>
						);
					})}
				</Tabs>
				<div className='flex items-center gap-3'>
					<FormControl size='small'>
						<Select
							value={status}
							onChange={(e) => { return setStatus(e.target.value); }}
						>
							{STATUSES.map((opt) => {
								return (
									<MenuItem key={opt.value} value={opt.value}>
										{opt.label}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
					<ToggleButtonGroup
						exclusive
						size='small'
						value={activeView.value}
						onChange={handleViewChange}
						sx={VIEW_TOGGLE_SX}
					>
						{VIEWS.map((view) => {
							const Icon = view.icon;
							return (
								<ToggleButton key={view.value} value={view.value}>
									<Icon className='size-4 mr-2' />
									{view.label}
								</ToggleButton>
							);
						})}
					</ToggleButtonGroup>
					<Chip size='small' color='success' label={`Completed: ${completedCount}`} />
					<Chip size='small' color='warning' label={`Pending: ${filteredKits.length - completedCount}`} />
					<span className='text-[16px]'>
						Total:
						{' '}
						<span className='font-semibold'>{filteredKits.length}</span>
						{' kits'}
					</span>
					{isItemView && (
						<span className='text-[16px]'>
							Items:
							{' '}
							<span className='font-semibold'>{itemGroups.length}</span>
						</span>
					)}
				</div>
			</div>

			{/* section chips over the loaded listing, apart from the search bar and the filter drawer */}
			<div className='flex flex-wrap justify-between items-center gap-3 mb-3'>
				<SectionChips
					options={sectionOptions}
					counts={sectionCounts}
					value={sections}
					onChange={setSections}
				/>
				{isItemView && itemGroups.length > 0 && (
					<Button variant='outline' size='sm' className='ml-auto' onClick={toggleAllItems}>
						{allExpanded ? 'Collapse all' : 'Expand all'}
					</Button>
				)}
			</div>

			{isItemView ? (
				<ItemWiseTable
					groups={itemGroups}
					expanded={expandedItems}
					onToggle={toggleItem}
					emptyMessage={emptyMessage()}
				/>
			) : (
				<TableContainer component={Paper}>
					<Table size='small'>
						<TableHead>
							<TableRow>
								<TableCell>Order ID</TableCell>
								<TableCell>Kit Name</TableCell>
								<TableCell>Issued To</TableCell>
								<TableCell>Issuing Process</TableCell>
								<TableCell>Plan</TableCell>
								<TableCell>Scheduled Date</TableCell>
								<TableCell>Completed</TableCell>
								<TableCell sx={{
									minWidth: '10rem',
								}}
								>
									Completion
								</TableCell>
								<TableCell align='right'>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredKits.length === 0 && (
								<TableRow>
									<TableCell colSpan={9} align='center'>
										{emptyMessage()}
									</TableCell>
								</TableRow>
							)}
							{filteredKits.map((kit) => {
								return (
									<TableRow key={kit.id}>
										<TableCell>{kit.orderId}</TableCell>
										<TableCell>{kit.kitName || '-'}</TableCell>
										<TableCell>{kit.fromProcessName || '-'}</TableCell>
										<TableCell>{kit.toProcessName || '-'}</TableCell>
										<TableCell>
											{kit.productionPlanName || '-'}
											<div className='text-xs text-gray-500'>
												{`#${kit.productionPlanId}`}
											</div>
										</TableCell>
										<TableCell>{formatDateTime(kit.scheduledAt)}</TableCell>
										<TableCell>
											<Chip
												size='small'
												color={kit.isCompleted ? 'success' : 'warning'}
												label={kit.isCompleted ? 'Yes' : 'No'}
											/>
										</TableCell>
										<TableCell>
											<Tooltip
												arrow
												placement='left'
												enterDelay={200}
												componentsProps={ITEMS_TOOLTIP_PROPS}
												title={(
													<div className='min-w-[36rem]'>
														<div className='px-2 pt-1 pb-2 text-sm font-semibold'>
															{kit.kitName || 'Kit Items'}
														</div>
														<KitItemsTable items={kit.items} />
													</div>
												)}
											>
												<div className='cursor-default'>
													<div className='flex items-center gap-2'>
														<LinearProgress
															variant='determinate'
															value={Math.min(kit.completionPercentage || 0, 100)}
															color={kit.completionPercentage >= 100 ? 'success' : 'primary'}
															sx={{
																flex: 1,
																height: 6,
																borderRadius: 3,
															}}
														/>
														<span className='text-sm'>{`${kit.completionPercentage}%`}</span>
													</div>
													<div className='text-xs text-gray-500'>
														{`${kit.itemsIssued}/${kit.itemsTotal} items issued`}
													</div>
												</div>
											</Tooltip>
										</TableCell>
										<TableCell align='right'>
											<div className='flex justify-end'>
												<Tooltip title='Kit Items'>
													<IconButton onClick={() => { return setItemsKit(kit); }}>
														<List className='size-4' />
													</IconButton>
												</Tooltip>
												<Tooltip title='View (opens in new tab)'>
													<IconButton onClick={() => { return handleView(kit); }}>
														<ExternalLink className='size-4' />
													</IconButton>
												</Tooltip>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog
				open={!!itemsKit}
				onClose={() => { return setItemsKit(null); }}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle>
					{itemsKit?.kitName || 'Kit Items'}
					<div className='text-sm text-gray-600 font-normal'>
						{`Order ${itemsKit?.orderId ?? '-'} · Issuing Process: ${itemsKit?.toProcessName || '-'} · Issued To: ${itemsKit?.fromProcessName || '-'}`}
					</div>
				</DialogTitle>
				<DialogContent dividers>
					<KitItemsTable items={itemsKit?.items} />
				</DialogContent>
				<DialogActions>
					<Button variant='outline' onClick={() => { return setItemsKit(null); }}>Close</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default KitIssuance;
